import React, { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';

/**
 * Individual stock particle in 3D space
 * Represents one stock as an arrow pointing in the direction of momentum
 * Arrow size represents trading volume
 * Arrow color represents industry/sector
 */
export const StockParticle = React.forwardRef(
  ({ stock, timeStep = 0, onHover, onUnhover, onClick }, ref) => {
    const groupRef = useRef();
    const arrowRef = useRef();
    const [isHovered, setIsHovered] = useState(false);

    // Get position and data from trajectory at current time step
    const getCurrentData = () => {
      if (stock.trajectory && stock.trajectory[timeStep]) {
        const point = stock.trajectory[timeStep];
        return {
          position: [point.x, point.y, point.z],
          momentum: point.momentum || 0,
          volatility: point.volatility || 0.02,
          volume: point.volume || 50000000,
          price: point.price || 100,
        };
      }
      return {
        position: [stock.x, stock.y, stock.z],
        momentum: 0,
        volatility: 0.02,
        volume: 50000000,
        price: stock.price || 100,
      };
    };

    const currentData = getCurrentData();
    const currentPos = currentData.position;

    // Size based on volume (market activity)
    const baseSize = 0.4;
    const volumeScale = Math.max(0.5, Math.min(2, currentData.volume / 50000000));
    const arrowScale = baseSize * volumeScale;

    // Arrow direction based on momentum
    // Positive momentum = up arrow, negative = down arrow
    // Use very low threshold since momentum values are small
    const arrowDirection = currentData.momentum > 0 ? 1 : currentData.momentum < 0 ? -1 : 0;
    const momentumStrength = Math.min(1, Math.abs(currentData.momentum) * 20); // Normalize to 0-1

    // Update userData for raycasting
    useEffect(() => {
      if (arrowRef.current) {
        arrowRef.current.userData = {
          isStock: true,
          stock,
          onHover: () => {
            setIsHovered(true);
            onHover?.(stock);
          },
          onHoverEnd: () => {
            setIsHovered(false);
            onUnhover?.();
          },
        };
      }
    }, [stock, onHover, onUnhover]);

    // Animation: scale on hover and flip based on momentum
    useFrame(() => {
      if (groupRef.current) {
        // Scale on hover (X and Z for size)
        const targetScale = isHovered ? arrowScale * 1.5 : arrowScale;
        groupRef.current.scale.x = groupRef.current.scale.x * 0.9 + targetScale * 0.1;
        groupRef.current.scale.z = groupRef.current.scale.z * 0.9 + targetScale * 0.1;

        // Flip on Y-axis based on momentum direction
        // Positive momentum = normal (scale.y = 1)
        // Negative momentum = flipped upside down (scale.y = -1)
        const targetScaleY = arrowDirection < 0 ? -1 : 1;
        groupRef.current.scale.y = groupRef.current.scale.y * 0.85 + targetScaleY * 0.15;
      }
    });

    const handleClick = () => {
      onClick?.(stock);
    };

    return (
      <group position={currentPos} ref={ref}>
        {/* Arrow representation of stock with momentum direction */}
        <group ref={groupRef} onClick={handleClick} onPointerEnter={() => setIsHovered(true)} onPointerLeave={() => setIsHovered(false)}>
          {/* Arrow shaft (cylinder) */}
          <mesh ref={arrowRef} position={[0, 0, 0]}>
            <cylinderGeometry args={[0.08, 0.08, 0.6, 8]} />
            <meshPhongMaterial
              color={stock.color}
              emissive={isHovered ? stock.color : '#000000'}
              emissiveIntensity={isHovered ? 0.6 : 0.2}
              shininess={100}
            />
          </mesh>

          {/* Arrow head (cone) pointing up */}
          <mesh position={[0, 0.4, 0]}>
            <coneGeometry args={[0.15, 0.3, 8]} />
            <meshPhongMaterial
              color={stock.color}
              emissive={isHovered ? stock.color : '#000000'}
              emissiveIntensity={isHovered ? 0.6 : 0.2}
              shininess={100}
            />
          </mesh>

          {/* Glow effect - opacity based on volatility */}
          <mesh position={[0, 0, 0]} scale={1.4 * volumeScale}>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshBasicMaterial
              color={stock.color}
              opacity={0.15 + currentData.volatility * 1.5}
              transparent
              fog={false}
            />
          </mesh>
        </group>

        {/* Note: Stock label and detailed info shown in HUD panel */}
      </group>
    );
  }
);

StockParticle.displayName = 'StockParticle';

export default StockParticle;
